import { flexionar, pluralizar } from '@/shared/utils';

import type {
  DocumentoAssinaturas,
  EntradaDoApoioDoCabecalho,
  NegociacaoAssinaturas,
  ParticipacaoEncontrada,
  PessoaApresentada,
  TotaisDeAssinaturas,
} from '../types/negociacaoAssinaturasTypes';
import { nomeDaPessoa, rotuloDaRepresentacao } from './rotulosAssinatura';

export const TOTAIS_INDISPONIVEIS = 'Totais indisponíveis';

/** Milhar com ponto, como o design escreve as quantidades ("2.317", "5.000"). */
export const formatarQuantidade = (valor: number): string => valor.toLocaleString('pt-BR');

/** Contagem do cabeçalho da tabela (telas 9 e 10): "2.317 assinaturas faltando". */
function textoDasAssinaturasFaltando(faltantes: number): string {
  return `${formatarQuantidade(faltantes)} ${flexionar(faltantes, 'assinatura')} faltando`;
}

/**
 * Linha de apoio do cabeçalho da tabela, na ordem de precedência: a busca sem resultado repete o
 * termo (tela 13); a consulta com linhas mostra as assinaturas faltando (telas 9 e 10).
 */
export function apoioDoCabecalhoDaLista({
  semResultados,
  buscaVigente,
  respondeu,
  total,
  assinaturasFaltantes,
}: EntradaDoApoioDoCabecalho): string | null {
  if (semResultados && buscaVigente) {
    return `Nenhuma pessoa corresponde a “${buscaVigente}” com os filtros e o período aplicados.`;
  }
  if (respondeu && total > 0) return textoDasAssinaturasFaltando(assinaturasFaltantes);
  return null;
}

/** "812 registros" ao lado dos atalhos (tela 9). */
export function textoDosRegistros(total: number): string {
  return `${formatarQuantidade(total)} ${flexionar(total, 'registro')}`;
}

export function textoDasAssinadas(totais: TotaisDeAssinaturas): string {
  return `${totais.assinadas} de ${totais.total} assinadas`;
}

export function textoDasFaltantes(faltantes: number): string {
  if (faltantes === 0) return 'Nenhuma faltando';
  return `${flexionar(faltantes, 'Falta', 'Faltam')} ${faltantes}`;
}

export function textoDaNegociacao(item: NegociacaoAssinaturas): string {
  const documentos = pluralizar(item.documentos.length, 'documento');
  return item.contraparteNome ? `${item.contraparteNome} · ${documentos}` : documentos;
}

export function papelERepresentacao(
  rotulo: string,
  representacao: ParticipacaoEncontrada['representacao'],
): string {
  return `${rotulo} · ${rotuloDaRepresentacao(representacao)}`;
}

export function pessoaNaVezApresentada(item: NegociacaoAssinaturas): PessoaApresentada | null {
  if (!item.naVez) return null;
  return {
    nome: nomeDaPessoa(item.naVez.nome),
    papel: papelERepresentacao(item.naVez.rotulo, item.naVez.representacao),
  };
}

export function pessoasEncontradas(item: NegociacaoAssinaturas): number {
  return item.documentos.reduce((soma, doc) => soma + doc.participacoesTotal, 0);
}

export const estaPendente = (participacao: ParticipacaoEncontrada): boolean =>
  participacao.situacao !== 'ASSINOU';

export function textoDasPendencias(item: NegociacaoAssinaturas): string {
  if (item.documentos.some((documento) => documento.totais === null)) return TOTAIS_INDISPONIVEIS;
  const documentos = item.documentos.filter((documento) => (documento.totais?.faltantes ?? 0) > 0);
  const assinaturas = documentos.reduce(
    (soma, documento) => soma + (documento.totais?.faltantes ?? 0),
    0,
  );
  return `${pluralizar(assinaturas, 'assinatura pendente', 'assinaturas pendentes')} em ${pluralizar(documentos.length, 'documento')}`;
}

export function quemJaAssinou(item: NegociacaoAssinaturas): string[] {
  return item.documentos.flatMap((documento) =>
    documento.participacoesEncontradas
      .filter((participacao) => !estaPendente(participacao))
      .map((participacao) => nomeDaPessoa(participacao.nome)),
  );
}

export const precisaDeConferencia = (documento: DocumentoAssinaturas): boolean =>
  documento.motivosConferencia.length > 0 ||
  documento.participacoesEncontradas.some((participacao) => participacao.aConferir);

export function documentosAConferir(item: NegociacaoAssinaturas): number {
  return item.documentos.filter(precisaDeConferencia).length;
}

/**
 * A busca mostra só parte das pessoas, mas o progresso do documento conta todas (CA-30). O aviso
 * aparece quando a diferença existe de fato.
 */
export function avisoDePessoasParciais(documento: DocumentoAssinaturas): string | null {
  if (!documento.totais) return null;
  const encontradas = documento.participacoesTotal;
  if (encontradas >= documento.totais.total) return null;
  return `Mostrando ${encontradas} de ${pluralizar(documento.totais.total, 'pessoa')}. Os totais contam todas as pessoas do documento.`;
}

/** Avisos de documento do rodapé da expansão (tela 10). */
export function avisosDoDocumento(documento: DocumentoAssinaturas): string[] {
  return documento.motivosConferencia.map((motivo) =>
    motivo === 'SELO_DIVERGENTE'
      ? `${documento.documentoNome}: marcado completo, ${textoDasFaltantes(documento.totais?.faltantes ?? 0).toLowerCase()}`
      : `${documento.documentoNome}: pessoas não identificadas`,
  );
}

export function documentosJaCompletos(item: NegociacaoAssinaturas): string[] {
  return item.documentos
    .filter((documento) => documento.completo && documento.motivosConferencia.length === 0)
    .map((documento) => documento.documentoNome);
}
